import * as React from "react";
const { useState, useEffect, useCallback, useRef } = React;
import { PanelRightOpen, PanelRightClose, X, Menu } from "lucide-react";
import GenAIChatInterface from "./GenAIChatInterface";
const MIN_PERCENT = 40;
const MAX_PERCENT = 60;

// Read current route params from Angular
const getRouteContext = () => {
  try {
    const el = angular.element(document.querySelector("body"));
    const injector = el.injector();
    if (injector) {
      const routeParams = injector.get("$routeParams");
      return {
        collection: routeParams.collection || "",
        recordId: routeParams.id || "",
      };
    }
  } catch (e) {
    // Angular not ready yet
  }
  return { collection: "", recordId: "" };
};

const SplitPanel = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isDevZone, setIsDevZone] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [panelWidthPx, setPanelWidthPx] = useState(() => Math.floor(window.innerWidth * MIN_PERCENT / 100));
  const [chatComponent, setChatComponent] = useState(() => GenAIChatInterface);
  const [routeCtx, setRouteCtx] = useState({ collection: "", recordId: "" });
  const isDragging = useRef(false);

  const getMinPx = () => Math.floor(window.innerWidth * MIN_PERCENT / 100);
  const getMaxPx = () => Math.floor(window.innerWidth * MAX_PERCENT / 100);

  const updateContentMargin = (width) => {
    const content = document.getElementById("content");
    if (content) {
      content.style.marginRight = width > 0 ? `${width}px` : "0px";
    }
  };

  // Check login state from Angular rootScope and current route
  useEffect(() => {
    const checkState = () => {
      try {
        const isDev = window.location.pathname === '/page' && window.location.search.includes('pagename=devzone');
        setIsDevZone(isDev);
      } catch (e) { }

      try {
        const el = angular.element(document.querySelector("body"));
        if (el && el.scope) {
          const rootScope = el.scope().$root;
          if (rootScope && rootScope.ob) {
            setIsLoggedIn(!rootScope.ob.isLoginOpen && rootScope.loadComplete);
          }
        }
      } catch (e) {
        // Angular not ready yet
      }
    };
    checkState();
    const interval = setInterval(checkState, 1000);
    return () => clearInterval(interval);
  }, []);

  // Upgrade to window.GenaiCreateChatInterface when it becomes available
  // useEffect(() => {
  //   if (window.GenaiCreateChatInterface) {
  //     setChatComponent(() => window.GenaiCreateChatInterface);
  //     return;
  //   }
  //   const interval = setInterval(() => {
  //     if (window.GenaiCreateChatInterface) {
  //       setChatComponent(() => window.GenaiCreateChatInterface);
  //       clearInterval(interval);
  //     }
  //   }, 1000);
  //   return () => clearInterval(interval);
  // }, []);

  // Update route context when panel opens and on route changes
  useEffect(() => {
    if (!isOpen) return;
    setRouteCtx(getRouteContext());

    const onRouteChange = () => setRouteCtx(getRouteContext());
    try {
      const el = angular.element(document.querySelector("body"));
      const rootScope = el.scope().$root;
      const deregister = rootScope.$on("$routeChangeSuccess", () => {
        setTimeout(onRouteChange, 0);
      });
      return () => deregister();
    } catch (e) {
      // fallback: no listener
    }
  }, [isOpen]);

  // Keep panel width within bounds on window resize
  useEffect(() => {
    const onResize = () => {
      if (!isOpen) return;
      setPanelWidthPx((prev) => {
        const clamped = Math.max(getMinPx(), Math.min(prev, getMaxPx()));
        updateContentMargin(clamped);
        return clamped;
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isOpen]);

  const toggleLeftMenu = useCallback(() => {
    const nav = document.querySelector("nav.left-menu");
    if (nav) {
      nav.classList.toggle("left-menu-showed");
      setMenuOpen((prev) => !prev);
    }
  }, []);

  const togglePanel = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        const w = Math.max(getMinPx(), Math.min(panelWidthPx, getMaxPx()));
        setPanelWidthPx(w);
        updateContentMargin(w);
      } else {
        updateContentMargin(0);
      }
      return next;
    });
  }, [panelWidthPx]);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    updateContentMargin(0);
  }, []);

  // Drag handler
  const onDragStart = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;

    const content = document.getElementById("content");
    if (content) content.style.transition = "none";

    const onMouseMove = (moveEvent) => {
      if (!isDragging.current) return;
      const newWidth = window.innerWidth - moveEvent.clientX;
      const clamped = Math.max(getMinPx(), Math.min(newWidth, getMaxPx()));
      setPanelWidthPx(clamped);
      updateContentMargin(clamped);
    };

    const onMouseUp = () => {
      isDragging.current = false;
      const content = document.getElementById("content");
      if (content) content.style.transition = "0.2s cubic-bezier(0.4, 0, 0.2, 1)";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  // Render the chat component body
  const renderPanelBody = () => {
    return (
      <div style={{ flex: 1, overflow: "auto" }}>
        {React.createElement(chatComponent, {
          collection: routeCtx.collection,
          recordId: routeCtx.recordId,
          renderingSource: 'idetabs',
          handleAiAssist: closePanel
        })}
      </div>
    );
  };

  if (!isLoggedIn || isDevZone) {
    return null;
  }

  return (
    <>
      {/* Left menu toggle - visible only on small screens */}
      <style>{`
        .split-panel-menu-toggle { display: none; }
        @media (max-width: 1024px) {
          .split-panel-menu-toggle { display: flex !important; }
        }
      `}</style>
      <button
        className="split-panel-menu-toggle"
        onClick={toggleLeftMenu}
        title="Toggle menu"
        style={{
          position: "fixed",
          top: 5,
          left: 12,
          zIndex: 1050,
          width: 36,
          height: 36,
          borderRadius: 8,
          border: "1px solid rgba(255,255,255,0.15)",
          backgroundColor: "#1f1f1f",
          color: "#e0e0e0",
          cursor: "pointer",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
          transition: "background-color 0.15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#333")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1f1f1f")}
      >
        {React.createElement(Menu, { size: 18 })}
      </button>

      {/* Toggle Button - fixed top right */}
      <button
        onClick={togglePanel}
        title={isOpen ? "Close split view" : "Open split view"}
        style={{
          position: "fixed",
          top: 5,
          right: isOpen ? panelWidthPx + 12 : 12,
          zIndex: 1050,
          width: 36,
          height: 36,
          borderRadius: 8,
          border: "1px solid rgba(255,255,255,0.15)",
          backgroundColor: "#1f1f1f",
          color: "#e0e0e0",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: isDragging.current ? "none" : "right 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s",
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#333")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1f1f1f")}
      >
        {isOpen
          ? React.createElement(PanelRightClose, { size: 18 })
          : React.createElement(PanelRightOpen, { size: 18 })}
      </button>

      {/* Split Panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: panelWidthPx,
          height: "100vh",
          backgroundColor: "#141414",
          borderLeft: "1px solid rgba(255,255,255,0.1)",
          zIndex: 1040,
          transform: isOpen ? "translateX(0)" : `translateX(100%)`,
          transition: isDragging.current ? "none" : "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          display: "flex",
          flexDirection: "column",
          color: "#e0e0e0",
        }}
      >
        {/* Drag Handle */}
        <div
          onMouseDown={onDragStart}
          style={{
            position: "absolute",
            top: 0,
            left: -4,
            width: 8,
            height: "100%",
            cursor: "col-resize",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 4,
              height: 40,
              borderRadius: 2,
              backgroundColor: "rgba(255,255,255,0.15)",
              transition: "background-color 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.35)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.15)")}
          />
        </div>

        {/* Panel Header 
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600 }}>Assist</span>
          <button
            onClick={closePanel}
            style={{
              background: "none",
              border: "none",
              color: "#999",
              cursor: "pointer",
              padding: 4,
              display: "flex",
              alignItems: "center",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#e0e0e0")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#999")}
          >
            {React.createElement(X, { size: 16 })}
          </button>
        </div>*/}

        {/* Panel Body - GenaiCreateChatInterface */}
        {isOpen && renderPanelBody()}
      </div>
    </>
  );
};

export default SplitPanel;
